/**
 * Attachment uploads for the SubKrewe email composer.
 *
 * Files go straight from the browser to Cloud Storage under
 * `subkrewe-email-attachments/{uid}/{draftId}/{name}`. Storage rules pin that
 * `{uid}` to the signed-in captain, and the send endpoint re-checks the prefix
 * and re-reads size/type from object metadata — so nothing here is trusted,
 * and the checks below exist to give fast feedback, not to enforce anything.
 */

import { deleteObject, getStorage, ref as storageRef, uploadBytesResumable } from 'firebase/storage'
import { useCurrentUser, useFirebaseApp } from 'vuefire'
import type { SubkreweEmailAttachment } from '~/shared/types/subkreweEmail.js'
import {
  formatBytes,
  isAllowedAttachmentType,
  SUBKREWE_EMAIL_LIMITS,
} from '~/shared/utils/subkreweEmail.js'

export interface PendingAttachment extends SubkreweEmailAttachment {
  /** 0–100 while uploading, 100 once stored. */
  progress: number
  uploading: boolean
  error?: string
}

/** Keeps a recognizable name while removing anything path-like. */
function safeFileName(name: string): string {
  return name
    .replace(/[/\\]/g, '-')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120)
    || 'attachment'
}

export function useSubkreweEmailAttachments() {
  const currentUser = useCurrentUser()
  const draftId = ref(createDraftId())
  const files = ref<PendingAttachment[]>([])
  const errors = ref<string[]>([])

  const uploading = computed(() => files.value.some(file => file.uploading))
  const totalBytes = computed(() => files.value.reduce((sum, file) => sum + file.size, 0))
  const ready = computed(() => files.value.filter(file => !file.uploading && !file.error))

  function createDraftId(): string {
    // Matches SAFE_DRAFT_ID in the send endpoint.
    return globalThis.crypto?.randomUUID?.().replace(/-/g, '')
      ?? Math.random().toString(36).slice(2).padEnd(16, '0')
  }

  function bucket() {
    return getStorage(useFirebaseApp())
  }

  /**
   * Rejects a file the composer already knows will fail, so the captain finds
   * out before a multi-megabyte upload rather than after.
   */
  function precheck(file: File): string | null {
    if (files.value.length >= SUBKREWE_EMAIL_LIMITS.maxAttachments) {
      return `You can attach at most ${SUBKREWE_EMAIL_LIMITS.maxAttachments} files.`
    }
    if (!isAllowedAttachmentType(file.type)) {
      return `"${file.name}" is a file type we can't send.`
    }
    if (file.size > SUBKREWE_EMAIL_LIMITS.maxAttachmentBytes) {
      return `"${file.name}" is larger than ${formatBytes(SUBKREWE_EMAIL_LIMITS.maxAttachmentBytes)}.`
    }
    if (totalBytes.value + file.size > SUBKREWE_EMAIL_LIMITS.maxTotalAttachmentBytes) {
      return `Attachments would total more than ${formatBytes(SUBKREWE_EMAIL_LIMITS.maxTotalAttachmentBytes)}.`
    }
    return null
  }

  async function addFiles(incoming: File[]): Promise<void> {
    const uid = currentUser.value?.uid
    if (!uid) {
      errors.value = ['You need to be signed in to attach files.']
      return
    }

    errors.value = []

    for (const file of incoming) {
      const problem = precheck(file)
      if (problem) {
        errors.value.push(problem)
        continue
      }

      // Collisions would silently overwrite the earlier upload.
      const base = safeFileName(file.name)
      const taken = files.value.some(existing => existing.filename === base)
      const filename = taken ? `${Date.now()}-${base}` : base
      const storagePath = `subkrewe-email-attachments/${uid}/${draftId.value}/${filename}`

      const entry = reactive<PendingAttachment>({
        filename,
        storagePath,
        contentType: file.type,
        size: file.size,
        progress: 0,
        uploading: true,
      })
      files.value.push(entry)

      try {
        await new Promise<void>((resolve, reject) => {
          const task = uploadBytesResumable(storageRef(bucket(), storagePath), file, {
            contentType: file.type,
          })
          task.on(
            'state_changed',
            (snapshot) => {
              entry.progress = snapshot.totalBytes
                ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
                : 0
            },
            reject,
            () => resolve(),
          )
        })
        entry.progress = 100
      }
      catch (error) {
        entry.error = error instanceof Error ? error.message : 'Upload failed'
        errors.value.push(`"${filename}" failed to upload.`)
      }
      finally {
        entry.uploading = false
      }
    }
  }

  async function removeFile(entry: PendingAttachment): Promise<void> {
    files.value = files.value.filter(file => file !== entry)
    try {
      await deleteObject(storageRef(bucket(), entry.storagePath))
    }
    catch {
      // The object may never have landed (failed upload). The send endpoint
      // only attaches what the captain still lists, so a stray object is
      // harmless.
    }
  }

  /** Starts a fresh draft. Call after a successful send. */
  function reset(): void {
    files.value = []
    errors.value = []
    draftId.value = createDraftId()
  }

  return {
    draftId,
    files,
    errors,
    uploading,
    totalBytes,
    ready,
    addFiles,
    removeFile,
    reset,
  }
}
